/**
 * SalesTaxSection — the venue's sales-tax rate, edited in place like the other
 * Details sections.
 *
 * A rate of 0 is spelled out rather than left as a bare "0%", because 0 and "not
 * set" both charge nothing and the difference matters: 0 is a confirmed no-tax
 * jurisdiction, whereas an unset rate is a gap the banner at the top of the page
 * is asking someone to close.
 */
import { VenueValidationError, type VenueDetail, type VenuePatch } from '../../../../api/venues';
import { Field } from '../venueCreateFields';

import { EditSection } from './EditSection';
import { Input } from './Input';
import { ReadGrid } from './ReadGrid';
import { useFormDraft } from './use-form-draft';

interface SectionProps {
  venue: VenueDetail;
  readOnly: boolean;
  save: (patch: VenuePatch) => Promise<void>;
}

/** Format the rate for display, or null to let ReadGrid render "Not set". */
function formatRate(taxPercent: number | null): string | null {
  if (taxPercent === null) return null;
  if (taxPercent === 0) return '0% — no sales tax in this jurisdiction';
  return `${taxPercent}%`;
}

export function SalesTaxSection({ venue, readOnly, save }: SectionProps) {
  const seed = { taxPercent: venue.taxPercent === null ? '' : String(venue.taxPercent) };
  const { draft, setField, reset } = useFormDraft(seed, venue.updatedAt);

  const saveRate = async () => {
    const rate = Number(draft.taxPercent);
    if (draft.taxPercent.trim() === '' || !Number.isFinite(rate)) {
      throw new Error('Enter the rate as a number, like 7.25.');
    }
    try {
      await save({ taxPercent: rate });
    } catch (err) {
      // Show the API's reason for the rate rather than a generic "Validation failed".
      if (err instanceof VenueValidationError && err.fieldErrors.taxPercent) {
        throw new Error(err.fieldErrors.taxPercent);
      }
      throw err;
    }
  };

  return (
    <EditSection
      title="Sales tax"
      hint="charged on every booking"
      readOnly={readOnly}
      onCancel={reset}
      onSave={saveRate}
      view={
        <ReadGrid
          cols={1}
          rows={[
            {
              label: 'Sales-tax rate',
              value: formatRate(venue.taxPercent),
              mono: true,
              full: true,
            },
          ]}
        />
      }
      form={
        <Field label="Sales-tax rate (%)">
          <Input
            mono
            value={draft.taxPercent}
            placeholder="7.25"
            onChange={(v) => setField('taxPercent', v)}
          />
        </Field>
      }
    />
  );
}
